import { QuickCommand } from "@/components/QuickCommand/QuickCommand";
import { Callout, HeaderText, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { isActionAvailable } from "@/utils/available-actions";
import { useParams, useSearch } from "@tanstack/react-router";
import { RoomOptions } from "./RoomOptions/RoomOptions";
import { AddEpisodeOfCare } from "./components/AddEpisodeOfCare";
import { Assessment } from "./components/Assessment/Assessment";
import { CarePlan } from "./components/CarePlan/CarePlan";
import { Diagnoses } from "./components/Diagnoses/Diagnoses";
import { EpisodeOfCare } from "./components/EpisodeOfCare";
import { Objective } from "./components/Objective/Objective";
import { SendAllToPharmacyButton } from "./components/Rx/SendAllToPharmacyButton";
import { Subjective } from "./components/Subjective/Subjective";
import { VisitClosureDialogProvider } from "./components/VisitClosure/VisitClosureDialog";
import { VisitTimer } from "./components/VisitTimer";
import {
  ChartingAvailableAction,
  useChartingAvailableActions,
} from "./components/network/useChartingAvailableActions";
import { useEpisodesOfCare } from "./network/useEpisodesOfCare";

export const Chart = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const {
    data: isSingleEncounterVisitsEnabled,
    isError: isSingleEncounterVisitsError,
    isLoading: isSingleEncounterVisitsLoading,
  } = useConfigCatFeatureFlag("single-encounter-visits");

  const { data: chartingAvailableActions } = useChartingAvailableActions({
    patientId,
    motherEncounterId: encounterId as string,
  });

  const canWriteEpisodesOfCare = isActionAvailable(
    ChartingAvailableAction.WRITE_EPISODE_OF_CARE,
    chartingAvailableActions,
  );

  const canReadEpisodesOfCare =
    canWriteEpisodesOfCare ||
    isActionAvailable(
      ChartingAvailableAction.READ_EPISODE_OF_CARE,
      chartingAvailableActions,
    );

  const {
    data: eocData,
    isLoading,
    isError,
  } = useEpisodesOfCare(encounterId as string);

  const isEocDataResolved = !isLoading && !isError && eocData !== undefined;
  const allChildEncounterIds = [
    ...new Set(
      eocData?.episodeOfCareResources.flatMap((resource) =>
        (Array.isArray(resource.childEncounter)
          ? resource.childEncounter
          : [resource.childEncounter]
        ).flatMap((childEncounter) =>
          childEncounter?.id?.trim() ? [childEncounter.id] : [],
        ),
      ) ?? [],
    ),
  ];
  const hasChildEncounters = allChildEncounterIds.length > 0;
  const isSingleEncounterVisit =
    isSingleEncounterVisitsEnabled === true &&
    isEocDataResolved &&
    !hasChildEncounters;

  const isSingleEncounterVisitsResolved =
    !isSingleEncounterVisitsLoading || isSingleEncounterVisitsError;

  const showAddEoc =
    canWriteEpisodesOfCare &&
    isSingleEncounterVisitsResolved &&
    (isSingleEncounterVisitsEnabled !== true ||
      (isEocDataResolved && hasChildEncounters));
  const showSendAllToPharmacyInFooter =
    isSingleEncounterVisitsEnabled !== true ||
    (isEocDataResolved && hasChildEncounters);

  return (
    <VisitClosureDialogProvider>
      <div
        className="w-full min-h-0 flex flex-col border rounded-md min-w-[33.33%]"
        data-testid="patient-chart"
      >
        <QuickCommand />
        <div className="flex items-center justify-between p-2 py-3">
          <div className="flex items-center gap-2">
            <HeaderText textColor="tertiary" size="sm">
              Visit Note
            </HeaderText>
            <VisitTimer />
          </div>
          <RoomOptions />
        </div>

        <div className="p-2 overflow-auto h-full">
          <div className="flex flex-col gap-3">
            <Subjective />
            <Objective />
            {isSingleEncounterVisit && <Diagnoses />}
            {isSingleEncounterVisit && (
              <CarePlan
                patientId={patientId}
                encounterId={encounterId as string}
                motherEncounterId={encounterId as string}
                enabled
              />
            )}
            {isSingleEncounterVisit && (
              <Assessment encounterId={encounterId as string} />
            )}
            {isError && (
              <Callout
                className="w-full cursor-pointer"
                variant="danger"
                title="Unable to load episodes of care"
                description="Please try again later."
              />
            )}

            {isLoading && <Skeleton className="h-32" />}

            {hasChildEncounters &&
              canReadEpisodesOfCare &&
              eocData?.episodeOfCareResources.map((episodeOfCare) => {
                return (
                  <EpisodeOfCare
                    episodeOfCare={episodeOfCare}
                    key={episodeOfCare.condition.id}
                  />
                );
              })}

            <div
              className={cn(
                "flex gap-2",
                showAddEoc ? "justify-between" : "justify-end",
                "[&:has(>button:first-child)]:flex-row",
                "[&:has(>div:first-child)]:flex-col",
              )}
            >
              {showAddEoc && <AddEpisodeOfCare />}
              {showSendAllToPharmacyInFooter &&
                (isSingleEncounterVisitsEnabled === true ? (
                  <SendAllToPharmacyButton
                    encounterId={encounterId as string}
                  />
                ) : (
                  <SendAllToPharmacyButton
                    childEncounterIds={allChildEncounterIds}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </VisitClosureDialogProvider>
  );
};
